// Calculate dosha scores from quiz answers
export const calculateDoshaScores = (answers) => {
  const scores = { vata: 0, pitta: 0, kapha: 0 };
  let totalQuestions = 0;

  // Sum up all the scores from answers
  Object.values(answers).forEach(categoryAnswers => {
    Object.values(categoryAnswers).forEach(answer => {
      if (answer && typeof answer === 'object') {
        scores.vata += answer.vata || 0;
        scores.pitta += answer.pitta || 0;
        scores.kapha += answer.kapha || 0;
        totalQuestions++;
      }
    });
  });

  // Convert to percentages
  const totalScore = scores.vata + scores.pitta + scores.kapha;
  if (totalScore === 0) return { vata: 33, pitta: 33, kapha: 34 };

  const percentages = {
    vata: Math.round((scores.vata / totalScore) * 100),
    pitta: Math.round((scores.pitta / totalScore) * 100),
    kapha: Math.round((scores.kapha / totalScore) * 100)
  };

  // Ensure percentages add up to 100
  const sum = percentages.vata + percentages.pitta + percentages.kapha;
  if (sum !== 100) {
    const diff = 100 - sum;
    // Add the difference to the highest percentage
    const highest = Object.keys(percentages).reduce((a, b) => 
      percentages[a] > percentages[b] ? a : b
    );
    percentages[highest] += diff;
  }

  return percentages;
};

// Determine primary and secondary doshas
export const analyzeDoshaConstitution = (scores) => {
  const doshas = [
    { name: 'vata', score: scores.vata },
    { name: 'pitta', score: scores.pitta },
    { name: 'kapha', score: scores.kapha }
  ].sort((a, b) => b.score - a.score);

  const primary = doshas[0];
  const secondary = doshas[1];
  const tertiary = doshas[2];

  // Determine constitution type
  let constitution;
  let constitutionType;

  // Single dosha dominance (>50%)
  if (primary.score >= 50) {
    constitution = primary.name;
    constitutionType = 'single';
  }
  // Dual dosha (primary >40% and secondary >30%)
  else if (primary.score >= 40 && secondary.score >= 30) {
    constitution = `${primary.name}-${secondary.name}`;
    constitutionType = 'dual';
  }
  // Tridoshic (all three relatively balanced, none >45%)
  else if (primary.score <= 45) {
    constitution = 'vata-pitta-kapha';
    constitutionType = 'tridoshic';
  }
  // Default to dual dosha
  else {
    constitution = `${primary.name}-${secondary.name}`;
    constitutionType = 'dual';
  }

  return {
    primaryDosha: primary.name,
    secondaryDosha: secondary.name,
    tertiaryDosha: tertiary.name,
    constitution,
    constitutionType,
    scores: {
      vata: scores.vata,
      pitta: scores.pitta,
      kapha: scores.kapha
    }
  };
};

// Get dosha balance analysis
export const getDoshaBalance = (scores) => {
  const analysis = analyzeDoshaConstitution(scores);
  const { primaryDosha, constitution, constitutionType } = analysis;

  let balanceDescription;
  let recommendations = [];

  switch (constitutionType) {
    case 'single':
      balanceDescription = `You have a clear ${primaryDosha.toUpperCase()} constitution. This means ${primaryDosha} qualities are dominant in your nature.`;
      recommendations.push(`Focus on balancing ${primaryDosha} through appropriate lifestyle and dietary choices`);
      break;
    
    case 'dual':
      const [primary, secondary] = constitution.split('-');
      balanceDescription = `You have a dual constitution with ${primary.toUpperCase()} as your primary dosha and ${secondary.toUpperCase()} as secondary.`;
      recommendations.push(`Balance both ${primary} and ${secondary} qualities`);
      recommendations.push(`Pay attention to seasonal changes that may affect both doshas`);
      break;
    
    case 'tridoshic':
      balanceDescription = `You have a rare tridoshic constitution with all three doshas relatively balanced.`;
      recommendations.push(`Maintain overall balance through seasonal awareness`);
      recommendations.push(`Adapt your lifestyle based on current imbalances rather than constitution`);
      break;
    
    default:
      balanceDescription = `Your dosha analysis shows a ${primaryDosha.toUpperCase()}-dominant constitution.`;
  }

  return {
    ...analysis,
    balanceDescription,
    recommendations
  };
};

// Seasonal considerations
export const getSeasonalGuidance = (constitution, currentSeason = null) => {
  const season = currentSeason || getCurrentSeason();
  
  const seasonalAdvice = {
    spring: {
      vata: 'Spring can help ground Vata with its stable energy, but avoid getting caught in Kapha sluggishness.',
      pitta: 'Good time for Pitta to prepare for summer with cooling practices.',
      kapha: 'Prime time for Kapha detox and energizing practices as nature awakens.'
    },
    summer: {
      vata: 'Summer warmth is beneficial for Vata, but stay hydrated and avoid overheating.',
      pitta: 'Most challenging season for Pitta - focus on cooling foods and practices.',
      kapha: 'Good season for Kapha to stay active, but avoid excessive heat.'
    },
    fall: {
      vata: 'Most challenging season for Vata - focus on grounding, warming practices.',
      pitta: 'Good transition season for Pitta to cool down from summer.',
      kapha: 'Maintain activity levels as energy may start to decrease.'
    },
    winter: {
      vata: 'Continue grounding practices, stay warm and nourished.',
      pitta: 'Good season for Pitta to build strength without overheating.',
      kapha: 'Most challenging season for Kapha - stay active and avoid heaviness.'
    }
  };

  const primaryDosha = constitution.split('-')[0];
  return {
    season,
    advice: seasonalAdvice[season]?.[primaryDosha] || 'Focus on maintaining balance through seasonal awareness.',
    generalTips: getGeneralSeasonalTips(season)
  };
};

// Get current season (simplified)
const getCurrentSeason = () => {
  const month = new Date().getMonth() + 1; // 1-12
  if (month >= 3 && month <= 5) return 'spring';
  if (month >= 6 && month <= 8) return 'summer';
  if (month >= 9 && month <= 11) return 'fall';
  return 'winter';
};

// General seasonal tips
const getGeneralSeasonalTips = (season) => {
  const tips = {
    spring: [
      'Gentle detoxification',
      'Light, warming foods',
      'Increase physical activity',
      'Reduce heavy, oily foods'
    ],
    summer: [
      'Stay cool and hydrated',
      'Eat fresh, cooling foods',
      'Avoid excessive sun exposure',
      'Practice calming activities'
    ],
    fall: [
      'Establish regular routines',
      'Eat warm, nourishing foods',
      'Stay grounded and centered',
      'Prepare for winter transition'
    ],
    winter: [
      'Maintain warmth and moisture',
      'Eat warming, cooked foods',
      'Stay active indoors',
      'Practice self-nurturing'
    ]
  };
  
  return tips[season] || [];
};

// Export all functions
export default {
  calculateDoshaScores,
  analyzeDoshaConstitution,
  getDoshaBalance,
  getSeasonalGuidance
};
